import * as React from 'react';

import { useTranslation } from 'react-i18next';

import type { Computable, Measurement, VolumeUnit, WeightUnit } from '@klareospace/pharos-lib-types';
import { StackedCell, TextCell } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WeightVolumeCellProps {
  totalVolume?: Computable<Measurement<VolumeUnit>>;
  totalWeight?: Computable<Measurement<WeightUnit>>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WeightVolumeCell({ totalVolume, totalWeight }: WeightVolumeCellProps): React.JSX.Element {
  const { t } = useTranslation('common');

  const weight = resolveComputable(totalWeight);
  const volume = resolveComputable(totalVolume);

  if (weight === undefined && volume === undefined) {
    return <TextCell>—</TextCell>;
  }

  if (weight === undefined) {
    // volume is guaranteed defined at this point
    return (
      <span className="text-xs text-muted-foreground">
        {volume?.data.value} {t(`units.${volume?.data.unit}`)}
      </span>
    );
  }

  const volumeText = volume !== undefined ? `${volume.data.value} ${t(`units.${volume.data.unit}`)}` : undefined;

  return (
    <StackedCell
      className="text-sm"
      primary={`${weight.data.value} ${t(`units.${weight.data.unit}`)}`}
      secondary={volumeText}
    />
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Treat a Computable with a missing `data` (contract-violating API response) as absent.
function resolveComputable<T>(computable?: Computable<T>): Computable<T> | undefined {
  return computable?.data === undefined ? undefined : computable;
}
